import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addWitnessDefendants,
  editWitnessDefendants,
} from "services/caseServices";
import {
  CaseCreation,
  CaseDefendantLink,
  Exhibit,
  Reporter,
  Suspect,
  Victim,
  Witness,
  WitnessDefendants,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  action: "add" | "edit";
  selectedData?: WitnessDefendants;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
    exhibits: Exhibit[];
  };
  onClose: (refetch: boolean) => void;
};

const WitnessDefendantForm: FC<Props> = ({
  caseData,
  action,
  selectedData,
  caseRelatedParties,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [witnessed, setWitnessed] = useState<string>();
  const [defendantsForWitness, setDefendantsForWitness] = useState<any[]>();

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (selectedData) {
      setWitnessed(
        typeof selectedData?.witnessed_info === "object" &&
          selectedData?.witnessed_info !== null &&
          "uu_id" in selectedData.witnessed_info
          ? String((selectedData.witnessed_info as Witness).uu_id)
          : ""
      );
      setDefendantsForWitness(
        selectedData?.witnessed_defendants_info?.map((d) => d?.id)
      );
    }
  }, [selectedData]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">Add Witness</h2>

        <div className="flex flex-col mt-4">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Select Witness
          </label>
          <select
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            value={witnessed}
            onChange={(e) => {
              setWitnessed(e.target.value);
            }}
            disabled={action === "edit"}
          >
            <option value={""}>Select Witness</option>
            {caseRelatedParties?.witnesses.map((f) => (
              <option key={f?.uu_id} value={f?.uu_id}>
                {f.first_name} {f.middle_name} {f.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col mt-4">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Select Defendants
          </label>
          <select
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-[100px]"
            value={defendantsForWitness}
            onChange={(e) => {
              setDefendantsForWitness(
                Array.from(e.target.selectedOptions, (option) => option.value)
              );
            }}
            multiple
          >
            {caseData?.case_defendant_links &&
              caseData?.case_defendant_links.map(
                (caseLink: CaseDefendantLink) => (
                  <option key={caseLink?.id} value={caseLink?.id}>
                    Defendant {caseLink?.defendant_number} :{" "}
                    {caseLink?.defendant_info?.first_name}{" "}
                    {caseLink?.defendant_info?.middle_name}{" "}
                    {caseLink?.defendant_info?.last_name}
                  </option>
                )
              )}
          </select>
        </div>

        <div className="mt-4 flex justify-between gap-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
            onClick={() => {
              onClose(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                      font-[Poppins] cursor-pointer font-[500] w-full
                      text-base rounded-md hover:bg-white hover:text-[#E90101]
                      ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={loading}
            onClick={() => {
              setLoading(true);

              const api =
                action === "add" ? addWitnessDefendants : editWitnessDefendants;
              api({
                id: action === "add" ? undefined : selectedData?.id,
                witnessed: witnessed,
                case: caseData?.id,
                witnessed_defendants: defendantsForWitness,
              })
                .then(() => {
                  setLoading(false);
                  showTooltip("Successful!", "success");
                  setWitnessed("");
                  setDefendantsForWitness([]);
                  onClose(true);
                })
                .catch((error) => {
                  setLoading(false);
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandlerOnForm(error) ||
                      errorHandler(error) ||
                      "Something went wrong. Please try again.",
                    "error"
                  );
                });
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                {LoadingIcon}
                Loading...
              </div>
            ) : action === "add" ? (
              "Add"
            ) : (
              "Edit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WitnessDefendantForm;
